import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useT, lbl } from "../lib/i18n";
import { Plus, Trash2, Edit, KeyRound, Copy, ShieldOff, ShieldCheck } from "lucide-react";

import { tr } from "../lib/i18n";
import { SlabEditor, toApiSlabs } from "../components/SlabEditor";
const ROLES = ["admin", "management", "sales", "accounts", "viewer"];
const sel = "w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm";

function PayFields({ form, setForm, editing }) {
  const { t } = useT();
  return (
    <div className="grid gap-2" data-testid="user-pay-fields">
      <label className="text-sm">{t("com.pay_type")}{form.role === "sales" ? " *" : ""}
        <select data-testid="user-pay-type" className={sel} value={form.pay_type || ""} onChange={(e) => setForm({ ...form, pay_type: e.target.value })}>
          <option value="">—</option><option value="salary_commission">{t("com.salary_commission")}</option><option value="commission_only">{t("com.commission_only")}</option>
        </select></label>
      {form.pay_type && <>
        <label className="text-sm">{t("com.percent")}
          <input data-testid="user-commission-percent" inputMode="decimal" className={sel} value={form.commission_percent ?? ""} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })} /></label>
        <label className="text-sm">{t("com.base")}
          <select data-testid="user-commission-base" className={sel} value={form.commission_base || "sale_price"} onChange={(e) => setForm({ ...form, commission_base: e.target.value })}>
            <option value="sale_price">{t("com.base_sale")}</option><option value="profit">{t("com.base_profit")}</option>
          </select></label>
        {editing && <>
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!form.own_slabs} onChange={(e) => setForm({ ...form, own_slabs: e.target.checked, slabs: form.slabs?.length ? form.slabs : [{ from_cars: 1, to_cars: "", percent: "" }] })} data-testid="user-own-slabs" /> {t("com.own_slabs")}</label>
          {form.own_slabs && <SlabEditor slabs={form.slabs || []} onChange={(slabs) => setForm({ ...form, slabs })} testid="user-slabs" />}
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={!!form.target_enabled} onChange={(e) => setForm({ ...form, target_enabled: e.target.checked })} data-testid="user-target-enabled" /> {t("com.target_on")}</label>
          {form.target_enabled && <div className="grid grid-cols-2 gap-2">
            <label className="text-sm">{t("com.target_cars")}<input dir="ltr" inputMode="numeric" className={sel} value={form.target_cars ?? ""} onChange={(e) => setForm({ ...form, target_cars: e.target.value.replace(/\D/g, "") })} data-testid="user-target-cars" /></label>
            <label className="text-sm">{t("com.target_value")}<input dir="ltr" inputMode="decimal" className={sel} value={form.target_value ?? ""} onChange={(e) => setForm({ ...form, target_value: e.target.value })} data-testid="user-target-value" /></label>
          </div>}
        </>}
      </>}
    </div>
  );
}

export default function UsersPage() {
  const { can, user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [tempPw, setTempPw] = useState(null);
  const resetPw = async (u) => {
    if (!window.confirm(tr("x.reset_the_password_for_they_will_be_sign", { p0: u.full_name }))) return;
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      setTempPw({ name: u.full_name, email: u.email, password: data.temporary_password });
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const reset2fa = async (u) => {
    if (!window.confirm(tr("mfa.reset_confirm", { p0: u.full_name }))) return;
    try {
      await api.post(`/users/${u.id}/2fa/reset`);
      toast.success(tr("mfa.reset_done"));
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", username: "", full_name: "", role: "viewer", password: "" });

  const reload = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { reload(); }, []);

  const submit = async () => {
    try {
      const pay = me.role === "admin" && ["sales", "management"].includes(form.role) && form.pay_type
        ? { pay_type: form.pay_type, commission_base: form.commission_base || "sale_price",
            ...(form.commission_percent !== "" && form.commission_percent != null ? { commission_percent: Number(form.commission_percent) } : {}),
            ...(editing ? { commission_slabs: form.own_slabs ? toApiSlabs(form.slabs || []) : [], target_enabled: !!form.target_enabled,
              ...(form.target_enabled && form.target_cars ? { target_cars: Number(form.target_cars) } : {}),
              ...(form.target_enabled && form.target_value ? { target_value: Number(form.target_value) } : {}) } : {}) } : {};
      if (editing) {
        const patch = { full_name: form.full_name, role: form.role, ...pay };
        await api.patch(`/users/${editing.id}`, patch);
        toast.success(tr("x.user_updated"));
      } else {
        const { pay_type, commission_percent, commission_base, own_slabs, slabs, target_enabled, target_cars, target_value, ...base } = form;
        await api.post("/users", { ...base, ...pay });
        toast.success(tr("x.user_created"));
      }
      setOpen(false);
      setEditing(null);
      setForm({ email: "", username: "", full_name: "", role: "viewer", password: "" });
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const del = async (u) => {
    if (!confirm(tr("x.delete_2", { p0: u.email }))) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(tr("x.user_deleted"));
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startEdit = (u) => {
    setEditing(u);
    setForm({ email: u.email, username: u.username || "", full_name: u.full_name, role: u.role, password: "", pay_type: u.pay_type || "", commission_percent: u.commission_percent ?? "", commission_base: u.commission_base || "sale_price", own_slabs: !!u.commission_slabs?.length, slabs: u.commission_slabs || [], target_enabled: !!u.target_enabled, target_cars: u.target_cars ?? "", target_value: u.target_value ?? "" });
    setOpen(true);
  };

  return (
    <div className="space-y-5" data-testid="users-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">{tr("x.team")}</div>
          <h1 className="text-3xl font-semibold" style={{ fontFamily: "Outfit" }}>{tr("x.users")}</h1>
        </div>
        {can("users", "create") && (
          <button
            data-testid="add-user-btn"
            onClick={() => { setEditing(null); setForm({ email: "", username: "", full_name: "", role: "viewer", password: "" }); setOpen(true); }}
            className="h-9 px-4 rounded-lg bg-accent-brand text-white text-sm font-semibold flex items-center gap-2"
          >
            <Plus size={14} /> {tr("x.add_user")}
          </button>
        )}
      </div>

      <div className="panel overflow-x-auto">
        <table className="w-full dense-table">
          <thead>
            <tr>
              <th>{tr("x.name")}</th>
              <th>{tr("x.email")}</th>
              <th>{tr("x.username")}</th>
              <th>{tr("x.role")}</th>
              <th>{tr("x.status")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.email}`}>
                <td className="font-medium">{u.full_name}{u.totp_enabled && <ShieldCheck size={13} className="inline ms-1.5 text-emerald-600" aria-label={tr("mfa.on")} data-testid={`user-2fa-on-${u.id}`} />}</td>
                <td>{u.email}</td>
                <td className="mono text-xs">{u.username || "—"}</td>
                <td><span className="pill pill-prep">{lbl(u.role)}</span></td>
                <td>{u.is_active ? <span className="pill pill-available">{tr("x.active")}</span> : <span className="pill pill-returned">{tr("x.disabled")}</span>}</td>
                <td><div className="flex gap-2 justify-end">
                  {can("users", "edit") && (
                    <button onClick={() => startEdit(u)} className="text-xs text-accent-brand flex items-center gap-1"><Edit size={12} /> {tr("x.edit")}</button>
                  )}
                  {me.role === "admin" && u.id !== me.id && (
                    <button onClick={() => resetPw(u)} data-testid={`reset-password-${u.id}`} className="text-xs text-accent-brand flex items-center gap-1"><KeyRound size={12} /> {tr("x.reset_password")}</button>
                  )}
                  {me.role === "admin" && u.id !== me.id && u.totp_enabled && (
                    <button onClick={() => reset2fa(u)} data-testid={`reset-2fa-${u.id}`} className="text-xs text-accent-brand flex items-center gap-1"><ShieldOff size={12} /> {tr("mfa.reset")}</button>
                  )}
                  {can("users", "delete") && u.id !== me.id && (
                    <button onClick={() => del(u)} className="text-xs text-red-600 flex items-center gap-1"><Trash2 size={12} /> {tr("x.delete")}</button>
                  )}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="panel p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="user-modal">
            <div className="eyebrow mb-3">{editing ? tr("x.edit_user") : tr("x.new_user")}</div>
            <div className="space-y-3">
              {!editing && (
                <>
                  <input data-testid="user-email" placeholder={tr("x.email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
                  <input placeholder={tr("x.username_optional")} value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
                </>
              )}
              <input data-testid="user-name" placeholder={tr("x.full_name_2")} value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
              <select data-testid="user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm">
                {ROLES.map((r) => <option key={r} value={r}>{lbl(r)}</option>)}
              </select>
              {me.role === "admin" && ["sales", "management"].includes(form.role) && <PayFields form={form} setForm={setForm} editing={!!editing} />}
              {!editing && (
                <input data-testid="user-password" type="password" placeholder={tr("x.temporary_password_10_characters")} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="w-full h-10 rounded-lg border border-[hsl(var(--border))] px-3 text-sm" />
              )}
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setOpen(false)} className="h-9 px-4 rounded-lg border border-[hsl(var(--border))] text-sm">{tr("x.cancel")}</button>
              <button data-testid="submit-user" onClick={submit} className="h-9 px-4 rounded-lg bg-accent-brand text-white text-sm font-semibold">{editing ? tr("x.save") : tr("x.create")}</button>
            </div>
          </div>
        </div>
      )}
      {tempPw && <TempPasswordModal info={tempPw} onClose={() => setTempPw(null)} />}
    </div>
  );
}

function TempPasswordModal({ info, onClose }) {
  const copy = () => navigator.clipboard?.writeText(info.password).then(() => toast.success(tr("x.copied"))).catch(() => {});
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="panel w-full max-w-md p-6 space-y-4" data-testid="temp-password-modal">
        <div className="kicker">{tr("x.temporary_password")}</div>
        <p className="text-sm">{tr("x.give_this_password_to")} <strong>{info.name}</strong> ({info.email}{tr("x.it_is_shown")} <strong>{tr("x.only_once")}</strong>{tr("x.they_must_change_it_when_they_sign_in")}</p>
        <div className="flex items-center gap-2">
          <code data-testid="temp-password-value" className="flex-1 px-3 py-2 text-base tracking-wider" style={{ background: "var(--surface)", border: "1px solid var(--divider)", fontFamily: "monospace" }}>{info.password}</code>
          <button className="btn-secondary" onClick={copy} data-testid="temp-password-copy"><Copy size={14} /> {tr("x.copy")}</button>
        </div>
        <div className="flex justify-end"><button className="btn-primary" onClick={onClose} data-testid="temp-password-done">{tr("x.done")}</button></div>
      </div>
    </div>
  );
}
